import { useCallback, useEffect, useRef, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { Button } from "@/components/Button";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { brokerApi, friendlyError, type SignContext, type SigningDocument } from "@/lib/api";

type Props = { onDone: () => void };

type DocView = { id: string; name: string | null; isPdf: boolean; src?: string; srcDoc?: string };

type Stage =
  | { kind: "loading" }
  | { kind: "onboarding" }
  | { kind: "document"; docs: DocView[] }
  | { kind: "otp" }
  | { kind: "signed" }
  | { kind: "error"; message: string };

function b64ToUtf8(b64: string): string {
  try {
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return "";
  }
}

function toDocView(d: SigningDocument): DocView {
  const type = (d.document_content_type ?? "").toLowerCase();
  const isPdf = type.includes("pdf");
  return {
    id: d.document_id,
    name: d.file_name,
    isPdf,
    ...(isPdf
      ? { src: `data:application/pdf;base64,${d.document_content_base64}` }
      : { srcDoc: b64ToUtf8(d.document_content_base64) }),
  };
}

export function Agreement({ onDone }: Props) {
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [inlineErr, setInlineError] = useState<string | null>(null);
  const started = useRef(false);
  // контекст подписи и otp_id держим на фронте — бэкенд stateless, ничего не теряется
  const signCtx = useRef<SignContext | null>(null);
  const otpId = useRef<string>("");

  // Загрузить документ оферты (онбординг уже выполнен)
  const loadDocument = useCallback(async () => {
    const pkg = await brokerApi.signingStart();
    if (pkg.already_signed) {
      setStage({ kind: "signed" });
      return;
    }
    if (!pkg.sign_context) {
      setStage({ kind: "error", message: "Не удалось получить документы. Попробуйте ещё раз." });
      return;
    }
    signCtx.current = pkg.sign_context;
    setStage({ kind: "document", docs: (pkg.documents ?? []).map(toDocView) });
  }, []);

  // Точка входа: проверяем статус → онбордим при необходимости → грузим документ
  const boot = useCallback(async () => {
    try {
      const st = await brokerApi.status();
      if (!st.active) {
        onDone(); // брокерская интеграция выключена — пропускаем
        return;
      }
      if (st.agreement_signed) {
        setStage({ kind: "signed" });
        return;
      }
      if (!st.onboarded) {
        setStage({ kind: "onboarding" });
        await brokerApi.onboard();
      }
      await loadDocument();
    } catch (e) {
      setStage({ kind: "error", message: friendlyError(e, "Не удалось подготовить соглашение. Напишите в поддержку.") });
    }
  }, [onDone, loadDocument]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void boot();
  }, [boot]);

  const requestOtp = useCallback(async () => {
    if (busy || !signCtx.current) return;
    setBusy(true);
    try {
      const r = await brokerApi.sendOtp(signCtx.current);
      otpId.current = r.otp_id;
      setOtp("");
      setStage({ kind: "otp" });
    } catch (e) {
      setStage({ kind: "error", message: friendlyError(e, "Не удалось отправить код. Попробуйте ещё раз.") });
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const confirm = useCallback(async () => {
    const code = otp.trim();
    if (code.length < 4 || busy || !signCtx.current || !otpId.current) return;
    setBusy(true);
    try {
      const r = await brokerApi.signingConfirm(signCtx.current, otpId.current, code);
      if (r.signed) setStage({ kind: "signed" });
      else setStage({ kind: "error", message: "Подпись не подтверждена. Попробуйте ещё раз." });
    } catch (e) {
      // неверный код приходит как 400 — даём повторить
      setOtp("");
      setStage({ kind: "otp" });
      setInlineError(friendlyError(e, "Неверный код. Проверьте письмо и попробуйте снова."));
    } finally {
      setBusy(false);
    }
  }, [otp, busy]);

  useBackButton(stage.kind === "signed" ? undefined : onDone);

  const mainBtn = ((): { text: string; onClick: () => void; active: boolean; visible: boolean; loading: boolean } => {
    // на загрузке — нативный спиннер Telegram (loading), текст кнопки не меняем,
    // иначе при смене текста на «Готово» остаётся визуальный «хвост»
    if (stage.kind === "document")
      return { text: "Подписать", onClick: requestOtp, active: !busy, visible: true, loading: busy };
    if (stage.kind === "otp")
      return { text: "Подтвердить", onClick: confirm, active: otp.trim().length >= 4 && !busy, visible: true, loading: busy };
    if (stage.kind === "signed") return { text: "Готово", onClick: onDone, active: true, visible: true, loading: false };
    return { text: "", onClick: () => {}, active: false, visible: false, loading: false };
  })();
  useMainButton(mainBtn);

  return (
    <Screen
      title={
        stage.kind === "signed"
          ? "Соглашение подписано ✓"
          : stage.kind === "otp"
          ? "Подтверждение по email"
          : stage.kind === "error"
          ? "Не получилось"
          : "Документы на подпись"
      }
      subtitle={
        stage.kind === "document"
          ? `Ознакомьтесь с документами (${stage.docs.length}). Для подписания отправим код на ваш email.`
          : stage.kind === "onboarding"
          ? "Регистрируем вас у брокера…"
          : undefined
      }
    >
      {(stage.kind === "loading" || stage.kind === "onboarding") && (
        <div className="flex flex-col items-center gap-3 py-16 text-white/45">
          <Spinner className="h-7 w-7" />
          <div className="text-[14px]">{stage.kind === "onboarding" ? "Создаём профиль у брокера…" : "Загрузка…"}</div>
        </div>
      )}

      {stage.kind === "document" && (
        <div className="space-y-3">
          {stage.docs.map((doc, i) => (
            <div key={doc.id} className="overflow-hidden rounded-2xl border border-white/10 bg-white">
              <div className="border-b border-black/10 bg-black/5 px-3 py-2 text-[13px] font-medium text-black/50">
                📄 {doc.name || `Документ ${i + 1}`}
              </div>
              <iframe
                title={doc.name || `Документ ${i + 1}`}
                {...(doc.isPdf ? { src: doc.src } : { srcDoc: doc.srcDoc })}
                sandbox=""
                className="h-[45vh] w-full"
              />
            </div>
          ))}
        </div>
      )}

      {stage.kind === "otp" && (
        <div className="space-y-3">
          <Note>Мы отправили код на ваш email. Введите его для подписания оферты.</Note>
          <input
            inputMode="numeric"
            autoFocus
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 8));
              setInlineError(null);
            }}
            placeholder="Код из письма"
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 py-3.5 text-center text-[20px] tracking-[0.3em] text-white outline-none placeholder:text-white/30 focus:border-white/20 [color-scheme:dark]"
          />
          {inlineErr && <div className="text-[13px] text-red-400">{inlineErr}</div>}
          <button onClick={requestOtp} disabled={busy} className="w-full py-2 text-center text-[13px] text-brand active:opacity-70">
            Отправить код повторно
          </button>
        </div>
      )}

      {stage.kind === "signed" && (
        <div className="rounded-2xl bg-emerald-500/10 p-5 text-center">
          <div className="text-3xl">✅</div>
          <div className="mt-2 text-[15px]">Договор-оферта подписан. Можно продолжать.</div>
        </div>
      )}

      {stage.kind === "error" && (
        <div className="space-y-3">
          <Note variant="danger">{stage.message}</Note>
          <Button
            onClick={() => {
              started.current = false;
              setStage({ kind: "loading" });
              void boot();
            }}
          >
            Попробовать снова
          </Button>
        </div>
      )}

      {stage.kind === "document" && <FallbackButton text="Подписать" onClick={requestOtp} loading={busy} />}
      {stage.kind === "otp" && (
        <FallbackButton text="Подтвердить" onClick={confirm} disabled={otp.trim().length < 4} loading={busy} />
      )}
      {stage.kind === "signed" && <FallbackButton text="Готово" onClick={onDone} />}
    </Screen>
  );
}
